import _ from 'lodash';
import NumberHelpers from '../../app/number-helpers';
import SummaryCard from '../reusable-elements/summary-card';
import { useContext, useMemo } from 'react';
import { IncomeAndExpenseContext } from './income-expense-container';
import { arrayAny } from '../../utils/arrayUtils';

export const PAndLTile = () => {

    const { data, fetchingData, chartSettings, fetchErrors } = useContext(IncomeAndExpenseContext);
    const pAndL = data ?? { received: [], spent: [] };
    const nh = new NumberHelpers();

    const getCurrentStats = () => {
        if (!pAndL || !arrayAny(pAndL.received) || !arrayAny(pAndL.spent)) {
            return { pl: 0, pcChange: 0 };
        }

        const currentIncome = _.minBy(pAndL.received, r => r.order);
        if (!currentIncome) return { pl: 0, pcChange: 0 };

        const currentExpense = pAndL.spent.find(s => s.period === currentIncome.period);
        if (!currentExpense) return { pl: 0, pcChange: 0 };

        const currentPL = currentIncome.grandTotal - Math.abs(currentExpense.grandTotal);

        const previousIncome = pAndL.received.find(r => r.order === currentIncome.order + 1);
        if (!previousIncome) return { pl: currentPL, pcChange: 0 };

        const previousExpense = pAndL.spent.find(s => s.period === previousIncome.period);
        if (!previousExpense) return { pl: currentPL, pcChange: 0 };

        const previousPL = previousIncome.grandTotal - Math.abs(previousExpense.grandTotal);

        // Calculate percentage change handling negative values
        const pc = previousPL !== 0
            ? ((currentPL - previousPL) / previousPL) * 100
            : currentPL > 0 ? 100 : currentPL < 0 ? -100 : 0;

        return { pl: currentPL, pcChange: pc };
    };

    const mergeSpentAndReceived = useMemo(() => {
        const result = [];

        pAndL.received.forEach(r => {
            const matchS = pAndL.spent.find(s => s.period === r.period);
            if (!matchS) return;

            result.push({
                order: matchS.order,
                period: r.period,
                value: r.grandTotal - Math.abs(matchS.grandTotal)
            });
        });

        return result;
    }, [pAndL.received, pAndL.spent]);

    const averagePAndL = useMemo(() => {
        if (!mergeSpentAndReceived || mergeSpentAndReceived.length === 0) {
            return 0;
        }

        const sum = _.sumBy(mergeSpentAndReceived, d => d.value);
        const average = sum / mergeSpentAndReceived.length;

        return isNaN(average) ? 0 : average;
    }, [mergeSpentAndReceived]);

    const maxPAndL = useMemo(() => {
        const result = _.maxBy(mergeSpentAndReceived, d => Math.abs(d.value));
        return result ? result.value : 0;
    }, [mergeSpentAndReceived]);

    const headLineFigures = useMemo(() => getCurrentStats(), [pAndL]);

    const orderedData = useMemo(() =>
        _.orderBy(mergeSpentAndReceived, pl => pl.order, 'desc').map(d => d.value),
        [mergeSpentAndReceived]
    );

    return (
        <SummaryCard
            loading={fetchingData}
            headerTitle="MONTHLY P&L"
            summaryValue={nh.convertNumberWithCommas(headLineFigures.pl, "£")}
            summaryValueMovement={headLineFigures.pcChange}
            summaryValueMovementSuffix="%"
            options={chartSettings.options}
            series={[{ data: orderedData }]}
            averageValue1={nh.convertNumberWithCommas(averagePAndL, "£")}
            averageValue1Label="Avg. P&L Per Month"
            averageValue2={nh.convertNumberWithCommas(maxPAndL, "£")}
            averageValue2Label="Max. P&L in range"
            error={fetchErrors.transactions}
        />

    );
};
